import { ApiError, type MerchantApi } from "./api";
import type { QueuedMutation } from "./domain";
import type { MerchantStorage } from "./offline-store";

export interface SyncResult {
  applied: string[];
  conflicted: string[];
  failed: string[];
  networkStopped: boolean;
}

export class SyncEngine {
  constructor(
    private readonly api: MerchantApi,
    private readonly storage: MerchantStorage,
  ) {}

  async enqueue(mutation: Omit<QueuedMutation, "attempts" | "state" | "statusMessage">) {
    const queued: QueuedMutation = {
      ...mutation,
      attempts: 0,
      state: "pending",
      statusMessage: null,
    };
    await this.storage.putMutation(queued);
    return queued;
  }

  async flush(): Promise<SyncResult> {
    const result: SyncResult = {
      applied: [],
      conflicted: [],
      failed: [],
      networkStopped: false,
    };
    const blockedEntities = new Set<string>();
    for (const mutation of await this.storage.getMutations()) {
      if (mutation.state !== "pending" || blockedEntities.has(mutation.entityKey)) continue;
      try {
        await this.api.request(mutation.path, {
          body: mutation.body,
          headers: mutation.headers,
          method: mutation.method,
        });
        await this.storage.deleteMutation(mutation.id);
        result.applied.push(mutation.id);
      } catch (error) {
        const apiError = error instanceof ApiError ? error : new ApiError("Network unavailable", 0);
        const updated = { ...mutation, attempts: mutation.attempts + 1 };
        if (apiError.status === 409) {
          await this.storage.putMutation({
            ...updated,
            state: "conflict",
            statusMessage: apiError.message,
          });
          blockedEntities.add(mutation.entityKey);
          result.conflicted.push(mutation.id);
          continue;
        }
        if (apiError.status === 0 || apiError.status >= 500) {
          await this.storage.putMutation(updated);
          result.networkStopped = true;
          break;
        }
        await this.storage.putMutation({
          ...updated,
          state: "failed",
          statusMessage: apiError.message,
        });
        blockedEntities.add(mutation.entityKey);
        result.failed.push(mutation.id);
      }
    }
    return result;
  }
}
